// Debug overlay. Toggle with backtick (`). Shows extended runtime info
// for balance work: entity counts by faction, sim/render budget, spawn
// pressure, current difficulty phase, ally HP histogram.

import Phaser from 'phaser';
import type { WorldState } from '../sim/world-state';
import { C } from '../sim/components';
import type { FactionComp, PickupComp, Projectile, Health, EmergentComp } from '../sim/components';
import { currentPhase, spawnBudget } from '../sim/systems/wave-director';

export class DebugOverlay {
  private text!: Phaser.GameObjects.Text;
  private bg!: Phaser.GameObjects.Rectangle;
  private visible = false;
  private lastSimMs = 0;

  constructor(private readonly scene: Phaser.Scene) {
    this.bg = scene.add
      .rectangle(8, 8, 260, 240, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setDepth(1500)
      .setVisible(false);
    this.text = scene.add
      .text(16, 14, '', {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#9be39b',
      })
      .setDepth(1501)
      .setVisible(false);

    scene.input.keyboard?.on('keydown-BACKTICK', () => this.toggle());
    scene.input.keyboard?.on('keydown-BACK_QUOTE', () => this.toggle()); // some browsers
  }

  toggle(): void {
    this.visible = !this.visible;
    this.bg.setVisible(this.visible);
    this.text.setVisible(this.visible);
  }

  recordSimMs(ms: number): void {
    // EWMA smoothing for stable readout.
    this.lastSimMs = this.lastSimMs * 0.9 + ms * 0.1;
  }

  draw(ws: WorldState): void {
    if (!this.visible) return;
    const { world } = ws;
    const factions = world.store<FactionComp>(C.faction);
    let allies = 0;
    let enemies = 0;
    factions.forEach((f) => {
      if (f.side === 'ally') allies++;
      else if (f.side === 'enemy') enemies++;
    });
    const projectiles = world.store<Projectile>(C.projectile).size;
    const pickups = world.store<PickupComp>(C.pickup).size;

    // Ally HP histogram in 4 buckets.
    const healths = world.store<Health>(C.health);
    const buckets = [0, 0, 0, 0];
    healths.forEach((h, id) => {
      if (factions.get(id)?.side !== 'ally') return;
      const frac = h.max > 0 ? h.current / h.max : 0;
      const idx = Math.min(3, Math.floor(frac * 4));
      buckets[idx] = (buckets[idx] ?? 0) + 1;
    });

    // Emergent trait counts across living allies.
    const emergents = world.store<EmergentComp>(C.emergent);
    const counts = new Map<string, number>();
    emergents.forEach((e, id) => {
      if (factions.get(id)?.side !== 'ally') return;
      for (const tid of e.ids) counts.set(tid, (counts.get(tid) ?? 0) + 1);
    });
    const topEmergents = [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([id, n]) => `  ${id} x${n}`)
      .join('\n');

    const fps = Math.round(this.scene.game.loop.actualFps);
    const phase = currentPhase(ws.time);
    const budget = spawnBudget(ws.time).toFixed(2);

    this.text.setText(
      [
        `fps ${fps}    sim ${this.lastSimMs.toFixed(1)}ms`,
        `time ${ws.time.toFixed(1)}s    phase ${phase}    budget ${budget}/s`,
        `entities ${world.entityCount}`,
        `  allies   ${allies}`,
        `  enemies  ${enemies}`,
        `  projectiles ${projectiles}`,
        `  pickups  ${pickups}`,
        `ally hp [0-25 25-50 50-75 75-100]`,
        `         ${buckets.join('     ')}`,
        `kills ${ws.killCount}    breeds ${ws.breedCount}`,
        topEmergents ? 'emergents:' : '(no emergents alive)',
        topEmergents,
      ]
        .filter(Boolean)
        .join('\n'),
    );
  }
}
